import { Flame, Palmtree, Snowflake } from 'lucide-react';

import type { OverallStreak } from '@/core/types';

export function StreakHero({ streak }: { streak: OverallStreak }) {
  const { current, freezes_available, pause } = streak;

  // A paused streak is not a running one, and the screen has to say which.
  // Hiding the pause would make the number unfalsifiable — the streak would
  // read the same whether it was earned or excused, which is the one thing a
  // streak must never do.
  const paused = pause !== null;
  const subline = paused
    ? pause.to_date
      ? `Paused until ${longDate(pause.to_date)}`
      : 'Paused — no return date set'
    : current > 0
      ? 'Keep it going'
      : 'Practice today to start your streak';

  return (
    <section className="flex flex-col items-center gap-3 py-8 text-center">
      <div className="flex items-center gap-3">
        {paused ? (
          <Palmtree className="text-success size-12" strokeWidth={2.2} aria-hidden />
        ) : (
          <Flame className="text-streak size-12" strokeWidth={2.2} aria-hidden />
        )}
        <div className="flex flex-col items-start leading-none">
          <span className="font-display text-5xl font-bold">{current}</span>
          <span className="text-muted-foreground mt-1 text-sm">day{current === 1 ? '' : 's'}</span>
        </div>
      </div>
      <p className="text-muted-foreground flex flex-wrap items-center justify-center gap-x-2 text-sm">
        <span>{subline}</span>
        {!paused && (
          <>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Snowflake className="size-3.5" />
              {freezes_available} freeze{freezes_available === 1 ? '' : 's'} left
            </span>
          </>
        )}
      </p>
      {paused && (
        // Said plainly rather than left to be inferred from a palm tree: the
        // whole promise of the pause is that the number will still be there.
        <p className="text-muted-foreground max-w-xs text-xs">
          These days are not counted against you. Practise anyway and they still count for you.
        </p>
      )}
    </section>
  );
}

/** "25 September" — the return date, in the unit a holiday is planned in. */
function longDate(iso: string): string {
  const [, month, day] = iso.split('-').map(Number);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${day} ${months[month - 1] ?? ''}`.trim();
}
